import { useCallback, useEffect, useMemo, useRef } from 'react';
import { findNearestWeekKey, getVenteWeekKey } from '../utils/kkpPlanningSemaineOptions';
import { getPlanningRowKey } from '../utils/kkpPlanningMergeUtils';

const SCROLL_MARGIN_TOP_PX = 88;
const MAX_SCROLL_RETRIES = 8;

/**
 * Scroll automatique vers la semaine la plus proche d'aujourd'hui dans un planning hebdomadaire.
 * Désactivé si un filtre semaine explicite est actif.
 */
export function usePlanningWeekAutoScroll({
  ventes,
  loading,
  filters,
  getRowKey = getPlanningRowKey,
}) {
  const rowRefs = useRef(new Map());
  const lastAutoScrollKeyRef = useRef(null);

  const semaineActive = Array.isArray(filters.semaine)
    ? filters.semaine.length > 0
    : Boolean(filters.semaine);
  const shouldAutoScroll = !semaineActive;
  const moisKey = Array.isArray(filters.mois) ? filters.mois.join(',') : filters.mois;
  const semaineKey = Array.isArray(filters.semaine) ? filters.semaine.join(',') : filters.semaine;
  const dateRangeKey = `${filters.date_debut ?? ''}|${filters.date_fin ?? ''}`;
  const anneeFromDate = filters.date_debut
    ? Number(String(filters.date_debut).slice(0, 4))
    : (filters.annee ? Number(filters.annee) : null);

  const anchorWeekKey = useMemo(() => {
    if (!shouldAutoScroll || ventes.length === 0) {
      return null;
    }
    return findNearestWeekKey(ventes, anneeFromDate);
  }, [ventes, anneeFromDate, shouldAutoScroll]);

  const anchorRowKey = useMemo(() => {
    if (!anchorWeekKey) {
      return null;
    }
    const row = ventes.find((v) => getVenteWeekKey(v) === anchorWeekKey);
    return row ? getRowKey(row) : null;
  }, [ventes, anchorWeekKey, getRowKey]);

  useEffect(() => {
    if (loading || !shouldAutoScroll || !anchorRowKey) {
      return;
    }

    const scrollKey = `${filters.annee ?? ''}|${moisKey}|${semaineKey}|${dateRangeKey}|${anchorRowKey}`;
    if (lastAutoScrollKeyRef.current === scrollKey) {
      return;
    }

    let cancelled = false;
    let retries = 0;

    const attemptScroll = () => {
      if (cancelled) {
        return;
      }

      const row = rowRefs.current.get(anchorRowKey);
      if (row) {
        lastAutoScrollKeyRef.current = scrollKey;
        row.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      if (retries < MAX_SCROLL_RETRIES) {
        retries += 1;
        window.requestAnimationFrame(attemptScroll);
      }
    };

    window.requestAnimationFrame(attemptScroll);

    return () => {
      cancelled = true;
    };
  }, [
    loading,
    shouldAutoScroll,
    anchorRowKey,
    filters.annee,
    moisKey,
    semaineKey,
    dateRangeKey,
    ventes.length,
  ]);

  const setRowRef = useCallback((rowKey, node) => {
    if (node) {
      rowRefs.current.set(rowKey, node);
    } else {
      rowRefs.current.delete(rowKey);
    }
  }, []);

  const getAnchorRowProps = useCallback((rowKey) => {
    if (anchorRowKey !== rowKey) {
      return {};
    }
    return {
      className: 'kkp-planning-week-anchor',
      style: { scrollMarginTop: `${SCROLL_MARGIN_TOP_PX}px` },
    };
  }, [anchorRowKey]);

  return {
    anchorRowKey,
    setRowRef,
    getAnchorRowProps,
  };
}
